import { FC, ReactNode } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
  [key: string]: unknown;
};

type WaveUISelectProps = SelectProps<string> & {
  name: string;
  label: string;
  value?: string;
  options: Option[];
  placeholder: string;
  helperText?: string;
  error?: boolean;
  onChange?: (event: SelectChangeEvent) => void;
  renderOption?: (option: Option) => ReactNode;
};

const WaveUISelect: FC<WaveUISelectProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  helperText,
  renderOption,
  fullWidth = true,
  error,
  sx,
  ...props
}) => {
  return (
    <FormControl
      variant='outlined'
      required={props.required}
      error={error}
      fullWidth={fullWidth}
      disabled={props.disabled}
      sx={sx}
    >
      <InputLabel htmlFor={`id_${name}`}>{label}</InputLabel>
      <Select
        displayEmpty
        name={name}
        label={label}
        inputProps={{ id: `id_${name}` }}
        value={value}
        onChange={onChange}
        renderValue={(value: string) => {
          if (!value) {
            return (
              <Typography variant='body1' color='grey.400'>
                {placeholder}
              </Typography>
            );
          }

          return options.find((option) => option.value === value)?.label;
        }}
        {...props}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {(renderOption && renderOption(option)) || option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default WaveUISelect;
